import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { FaSearch, FaTimes, FaSpinner, FaStar, FaTrash, FaPlus, FaRegStar, FaStarHalfAlt } from 'react-icons/fa';
import LoadingScreen from '../../components/LoadingScreen';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';
const token = localStorage.getItem('authToken');

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0
  }).format(amount);
}

function StarRating({ rating }) {
  const stars = [];
  const r = Number(rating || 0);
  for (let i = 1; i <= 5; i++) {
    if (r >= i) stars.push(<FaStar key={i} className="text-warning" style={{ fontSize: '10px' }} />);
    else if (r >= i - 0.5) stars.push(<FaStarHalfAlt key={i} className="text-warning" style={{ fontSize: '10px' }} />);
    else stars.push(<FaRegStar key={i} className="text-warning" style={{ fontSize: '10px' }} />);
  }
  return (
    <span className="d-inline-flex align-items-center gap-0 ms-1">
      {stars}
      <small className="text-muted ms-1" style={{ fontSize: '10px' }}>({r.toFixed(1)})</small>
    </span>
  );
}

const FeaturedProductsPage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [addingId, setAddingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const searchTimeout = useRef(null);

  const fetchFeaturedProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/featured-products`);
      setFeaturedProducts(response.data || []);
    } catch (error) {
      setMessage('Failed to fetch featured products.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  const handleSearchDebounced = (value) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => handleSearch(value), 400);
  };

  const handleSearch = async (value = searchTerm) => {
    if (!value) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/product-list?search=${encodeURIComponent(value)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const filtered = response.data.filter(prod =>
        !featuredProducts.some(f => f.name === prod.name && f.brand === prod.brand && f.category === prod.category)
      );
      setSearchResults(filtered);
    } catch (error) {
      setMessage('Failed to search products.');
    } finally {
      setSearching(false);
    }
  };

  const handleAddProduct = async (prod) => {
    setAddingId(prod._id);
    try {
      const payload = {
        productId: prod._id,
        name: prod.name, brand: prod.brand, category: prod.category,
        price: prod.price, quantity: prod.quantity, unit: prod.unit,
        description: prod.description,
        image1: prod.image1, image2: prod.image2, image3: prod.image3, image4: prod.image4,
        stock: prod.stock,
      };
      const response = await axios.post(`${API_BASE_URL}/featured-product`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage(response.data.message || 'Product added.');
      fetchFeaturedProducts();
      setSearchResults([]);
      setSearchTerm('');
    } catch (error) {
      setMessage('Failed to add product.');
    } finally {
      setAddingId(null);
    }
  };

  const handleRemoveProduct = async (id) => {
    setRemovingId(id);
    try {
      const response = await axios.delete(`${API_BASE_URL}/featured-product/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage(response.data.message || 'Product removed.');
      fetchFeaturedProducts();
    } catch (error) {
      setMessage('Failed to remove product.');
    } finally {
      setRemovingId(null);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setMessage('');
  };

  return (
    <div className="container-fluid p-3 pt-2 g-0 adma">

      {/* Header */}
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div className="section-title">
              <h4 className="mt-1">FEATURED PRODUCTS</h4>
              <p className="text-muted p-0 m-0">Add existing products to your featured product list</p>
            </div>
          </div>
        </div>
      </div>

      {/* Control Panel */}
      <div className="pending-control-panel">
        <div className="search-control-wrapper flex-grow-1" style={{ maxWidth: '450px', position: 'relative' }}>
          <input
            type="text"
            className="form-control search-input-custom w-100"
            placeholder="Search by product name, brand, or category..."
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              handleSearchDebounced(e.target.value);
            }}
          />
          <FaSearch className="search-icon-custom" />
          {searchTerm && (
            <button
              className="btn btn-link p-0"
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', zIndex: 2, color: '#94a3b8' }}
              onClick={handleClearSearch}
              tabIndex={-1}
            >
              <FaTimes />
            </button>
          )}
        </div>
        <div className="d-flex align-items-center">
          <small className="text-muted fw-semibold">
            {featuredProducts.length} featured product{featuredProducts.length !== 1 ? 's' : ''}
          </small>
        </div>
      </div>

      {/* Search Results Dropdown Table */}
      {searchTerm && (
        <div className="row mt-0">
          <div className="col-12">
            <div className="card order-table-card">
              <div className="card-header bg-white border-bottom d-flex align-items-center justify-content-between" style={{ borderColor: '#cbd5e1 !important', padding: '10px 16px' }}>
                <span className="fw-semibold text-muted" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Search Results
                </span>
                {searching && <FaSpinner className="fa-spin text-muted" style={{ fontSize: '13px' }} />}
              </div>
              {searching ? (
                <div className="text-center py-4">
                  <FaSpinner className="fa-spin text-danger mb-2" style={{ fontSize: '20px' }} />
                  <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>Searching products...</p>
                </div>
              ) : searchResults.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>PRODUCT</th>
                        <th>CATEGORY</th>
                        <th>BRAND</th>
                        <th>PRICE</th>
                        <th>STOCK</th>
                        <th className="text-end pe-4">ADD</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map((prod, idx) => (
                        <tr key={prod._id}>
                          <td className="fw-semibold text-muted">{idx + 1}</td>
                          <td>
                            <div className="d-flex gap-2 align-items-start">
                              {prod.image1 && (
                                <img
                                  src={prod.image1.startsWith('http') ? prod.image1 : `${API_BASE_URL}${prod.image1}`}
                                  alt={prod.name}
                                  style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, border: '1px solid #cbd5e1' }}
                                  onError={e => { e.currentTarget.style.opacity = '0.4'; }}
                                />
                              )}
                              <div>
                                <strong className="text-dark d-block">{prod.name}</strong>
                                <div className="d-flex flex-wrap align-items-center gap-1 mt-1">
                                  <span className="badge bg-light text-dark border" style={{ fontSize: '10px' }}>{prod.quantity} {prod.unit}</span>
                                  <StarRating rating={prod.rating} />
                                </div>
                              </div>
                            </div>
                          </td>
                          <td><span className="badge bg-info-subtle text-info border border-info-subtle px-2">{prod.category}</span></td>
                          <td><span className="badge bg-warning-subtle text-warning border border-warning-subtle px-2">{prod.brand}</span></td>
                          <td className="fw-bold text-success">{formatCurrency(prod.price)}</td>
                          <td>
                            <span className={`badge px-2 py-1 ${Number(prod.stock) <= 0 ? 'bg-danger' : Number(prod.stock) < 5 ? 'bg-warning text-dark' : 'bg-success'}`}>
                              {Number(prod.stock) <= 0 ? 'Out of Stock' : `${prod.stock} in stock`}
                            </span>
                          </td>
                          <td className="text-end pe-4">
                            <div className="order-actions-wrapper justify-content-end">
                              <button
                                className="order-action-btn order-action-btn-view"
                                onClick={() => handleAddProduct(prod)}
                                disabled={addingId === prod._id}
                                title="Add to Featured"
                              >
                                {addingId === prod._id ? <FaSpinner className="fa-spin" /> : <FaPlus />}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>No products found matching "{searchTerm}"</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Featured Products Table */}
      {loading ? (
        <LoadingScreen />
      ) : (
        <div className="row mt-0">
          <div className="col-12">
            <div className="card border-0 rounded-0 shadow-none order-table-card">
              <div className="table-responsive">
                <table className="table table-hover mb-0 align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>#</th>
                      <th>PRODUCT DETAILS</th>
                      <th>CATEGORY</th>
                      <th>BRAND</th>
                      <th>PRICE</th>
                      <th>STOCK</th>
                      <th className="text-end pe-4">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {featuredProducts.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-5">
                          <FaStar className="text-muted mb-3" style={{ fontSize: '2rem' }} />
                          <p className="text-muted mb-0">No featured products added yet.</p>
                          <small className="text-muted">Search for products above to add them here.</small>
                        </td>
                      </tr>
                    ) : (
                      featuredProducts.map((prod, idx) => {
                        const stockNum = Number(prod.stock ?? 0);
                        const lowStock = stockNum > 0 && stockNum < 5;
                        const outOfStock = stockNum <= 0;
                        return (
                          <tr key={prod._id} className={outOfStock ? 'table-danger' : lowStock ? 'table-warning' : ''}>
                            <td className="fw-semibold text-muted text-center">{idx + 1}</td>
                            <td>
                              <div className="d-flex gap-2 align-items-start">
                                {prod.image1 && (
                                  <img
                                    src={prod.image1.startsWith('http') ? prod.image1 : `${API_BASE_URL}${prod.image1}`}
                                    alt={prod.name}
                                    style={{ width: 45, height: 45, objectFit: 'cover', borderRadius: 4, border: '1px solid #cbd5e1' }}
                                    onError={e => { e.currentTarget.style.opacity = '0.4'; }}
                                  />
                                )}
                                <div>
                                  <strong className="text-dark d-block">{prod.name}</strong>
                                  <div className="d-flex flex-wrap align-items-center gap-1 mt-1">
                                    <span className="badge bg-light text-dark border" style={{ fontSize: '10px' }}>{prod.quantity} {prod.unit}</span>
                                    <StarRating rating={prod.rating} />
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td><span className="badge bg-info-subtle text-info border border-info-subtle px-2">{prod.category}</span></td>
                            <td><span className="badge bg-warning-subtle text-warning border border-warning-subtle px-2">{prod.brand}</span></td>
                            <td>
                              <strong className="text-success d-block">{formatCurrency(prod.price)}</strong>
                              {prod.originalPrice && prod.originalPrice > prod.price && (
                                <span className="text-muted text-decoration-line-through small" style={{ fontSize: '11px' }}>{formatCurrency(prod.originalPrice)}</span>
                              )}
                            </td>
                            <td>
                              <span className={`badge px-2 py-1 ${outOfStock ? 'bg-danger' : lowStock ? 'bg-warning text-dark' : 'bg-success'}`} style={{ fontSize: '0.8rem' }}>
                                {outOfStock ? 'Out of Stock' : lowStock ? `Low stock (${prod.stock})` : `${prod.stock} in stock`}
                              </span>
                            </td>
                            <td className="text-end pe-4">
                              <div className="order-actions-wrapper justify-content-end">
                                <button
                                  className="order-action-btn order-action-btn-cancel"
                                  onClick={() => handleRemoveProduct(prod._id)}
                                  disabled={removingId === prod._id}
                                  title="Remove from Featured"
                                >
                                  {removingId === prod._id ? <FaSpinner className="fa-spin" /> : <FaTrash />}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Message */}
      {message && (
        <div className="alert alert-info mt-3 mb-0 py-2 px-3 rounded-0 border-0" style={{ borderLeft: '3px solid #0ea5e9', background: '#f0f9ff', color: '#0369a1', fontSize: '0.875rem' }}>
          {message}
        </div>
      )}
    </div>
  );
};

export default FeaturedProductsPage;